/**
 * Server-side activity search box for the Takt board. Debounces the query,
 * hits GET .../activities/search, and on pick hands the result to the parent —
 * which opens the existing edit modal for that activity. The board never has
 * to contain the activity for it to be findable/editable here.
 */
import React from "react";
import { searchActivities } from "../../../api/clientV2";
import type { ActivitySearchResult, TaktColorEntry } from "../../../models";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { statusLabel } from "../../../utils/format";
import "./ActivitySearch.css";

interface Props {
  scheduleId: number;
  /** Takt color map (keyed by work code) so result pills match the board. */
  colors: Record<string, TaktColorEntry>;
  onSelect: (result: ActivitySearchResult) => void;
}

const DEBOUNCE_MS = 200;

const ActivitySearch: React.FC<Props> = ({ scheduleId, colors, onSelect }) => {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<ActivitySearchResult[]>([]);
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const wrapRef = React.useRef<HTMLDivElement>(null);
  // Monotonic id so a slow earlier request can't overwrite a newer result.
  const reqIdRef = React.useRef(0);

  useClickOutside(wrapRef, () => setOpen(false), open);

  React.useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const reqId = ++reqIdRef.current;
    const handle = setTimeout(() => {
      searchActivities(scheduleId, q)
        .then((rows) => {
          if (reqId !== reqIdRef.current) return;
          setResults(rows);
          setActiveIndex(0);
          setOpen(true);
        })
        .catch(() => {
          if (reqId !== reqIdRef.current) return;
          setResults([]);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, scheduleId]);

  const pick = React.useCallback(
    (r: ActivitySearchResult) => {
      onSelect(r);
      setQuery("");
      setResults([]);
      setOpen(false);
    },
    [onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[activeIndex]) {
      pick(results[activeIndex]);
    }
  };

  const trimmed = query.trim();
  const showDropdown = open && trimmed.length > 0;

  return (
    <div className="activity-search" ref={wrapRef}>
      <input
        type="text"
        className="activity-search-input"
        placeholder="Search activities…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (trimmed) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      {showDropdown && (
        <div className="activity-search-list">
          {results.length === 0 ? (
            <div className="activity-search-empty">No matches</div>
          ) : (
            results.map((r, i) => {
              const swatch = r.work_code ? colors[r.work_code] : undefined;
              return (
                <button
                  key={r.id}
                  type="button"
                  className={
                    "activity-search-item" +
                    (i === activeIndex ? " activity-search-item--active" : "")
                  }
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(r);
                  }}
                >
                  <span
                    className="activity-search-code"
                    style={
                      swatch
                        ? {
                            background: swatch.fill,
                            color: swatch.font,
                            borderColor: "transparent",
                          }
                        : undefined
                    }
                  >
                    {r.work_code || r.task_code}
                  </span>
                  <span className="activity-search-name">{r.task_name}</span>
                  <span className="activity-search-meta">
                    {[r.section_name, [r.takt_b, r.takt_c].filter(Boolean).join(" ")]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <span className="activity-search-status">
                    {statusLabel(r.status)}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ActivitySearch;
